import TableCell, { type TableCellProps } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import type { SortDir } from "../../hooks/useUrlState.ts";

export interface SortableColumn {
  /** Sort key sent to the API; null for a column that can't be sorted. */
  id: string | null;
  /** Header text; a node (e.g. text plus a settings button) needs a `key`. */
  label: ReactNode;
  /** React key; defaults to the label. */
  key?: string;
  align?: TableCellProps["align"];
  width?: number | string;
  sx?: SxProps<Theme>;
}

interface SortableTableHeadProps {
  columns: readonly SortableColumn[];
  sortBy: string;
  sortDir: SortDir;
  onSort: (column: string) => void;
  /** Arrow direction shown on inactive columns. */
  defaultDir?: SortDir;
  /** Cells before the columns, e.g. a select-all checkbox cell. */
  leading?: ReactNode;
}

export default function SortableTableHead({
  columns,
  sortBy,
  sortDir,
  onSort,
  defaultDir = "desc",
  leading,
}: SortableTableHeadProps) {
  return (
    <TableHead>
      <TableRow>
        {leading}
        {columns.map(({ id, label, key, align, width, sx }) => {
          const active = id !== null && sortBy === id;
          return (
            <TableCell
              key={key ?? String(label)}
              align={align}
              width={width}
              sx={sx}
              sortDirection={active ? sortDir : false}
            >
              {id ? (
                <TableSortLabel
                  active={active}
                  direction={active ? sortDir : defaultDir}
                  onClick={() => onSort(id)}
                >
                  {label}
                </TableSortLabel>
              ) : (
                label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
